import { CodeData } from "common/codedata/source/CodeDataSource";
import codeDataStore from "common/codedata/store/CodeDataStore";
import { toJS } from "mobx";
import React from "react";
import CommonUtil from "utils/CommonUtil";
import ObjectUtil from "utils/ObjectUtil";

export interface UICheckboxesProps {
  value?: string | string[];
  valueChanged?: (value: string, values: string[]) => void;
  valueSeparator?: string;
  checkAllLabel?: string;
  codeData?: CodeData[] | string;
  inline?: boolean;
  space?: string;
  className?: string;
}

UICheckboxes.defaultProps = {
  valueSeparator: ",",
  inline: true,
} as UICheckboxesProps;

export default UICheckboxes;

function UICheckboxes(props: UICheckboxesProps) {
  const [codeDatas, setCodeDatas] = React.useState<CodeData[]>();
  const [checkedMap] = React.useState<{ [key: string]: boolean }>({});
  const [checkAll, setCheckAll] = React.useState<boolean>();

  React.useEffect(
    () => {
      for (const key in checkedMap) {
        checkedMap[key] = false;
      }

      if (props.valueChanged != null && codeDatas != null) {
        props.valueChanged(ObjectUtil.undefined, []);
      }

      if (Array.isArray(toJS(props.codeData))) {
        setCodeDatas([...(props.codeData as CodeData[])]);
      } else if (typeof props.codeData === "string") {
        (async (codeDataKey: string) => {
          setCodeDatas(await codeDataStore.readOnce(codeDataKey));
        })(props.codeData);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(props.codeData)]
  );

  React.useEffect(
    () => {
      const values: string[] = (() => {
        if (props.value == null || props.value === "") {
          return [] as string[];
        }

        if (Array.isArray(toJS(props.value)) === true) {
          return props.value as string[];
        }

        if (typeof props.value === "string" && props.valueSeparator != null) {
          return props.value.split(props.valueSeparator).map((value) => value.trim());
        }

        return [] as string[];
      })();

      for (const key in checkedMap) {
        checkedMap[key] = false;
      }

      values.forEach((value) => (checkedMap[value] = true));

      setCheckAll(codeDatas?.findIndex((codeData) => checkedMap[codeData.code] !== true) === -1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.value]
  );

  const name = CommonUtil.getUid();

  return (
    <div className={props.className}>
      {props.checkAllLabel != null && (
        <label>
          <input
            type="checkbox"
            checked={checkAll === true}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              const _checkAll = event.target.checked === true;

              codeDatas?.forEach((codeData) => {
                checkedMap[codeData.code] = _checkAll;
              });

              if (props.valueChanged != null) {
                const values: string[] = _checkAll && codeDatas != null ? codeDatas.map((codeData) => codeData.code) : [];

                props.valueChanged(values.join(props.valueSeparator), values);
              }

              setCheckAll(_checkAll);
            }}
          />
          {props.checkAllLabel}
        </label>
      )}

      {codeDatas?.map((codeData: CodeData, index: number) => (
        <label key={codeData.code} className={ "mgl-10"}>
          <input
            type="checkbox"
            name={name}
            value={codeData.code}
            checked={checkedMap[codeData.code] === true}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              checkedMap[event.target.value] = event.target.checked === true;

              if (props.valueChanged != null) {
                const values: string[] = [];

                for (let value in checkedMap) {
                  if (checkedMap[value] === true) {
                    values.push(value);
                  }
                }

                props.valueChanged(values.join(props.valueSeparator), values);
              }
            }}
          />
          {codeData.name}
        </label>
      ))}
    </div>
  );
}
